/**
 * getToken.ts
 * Author: Arya Ingale
 * Date: 6 May 2026
 *
 * Fetches a Salesforce access token via a server-side proxy.
 * The proxy URL is configured via REACT_APP_SF_TOKEN_PROXY_URL.
 */
interface SalesforceTokenResponse {
	access_token: string
	instance_url: string
}

const TOKEN_PROXY_TIMEOUT_MS = 10_000

export const getToken = async (): Promise<SalesforceTokenResponse> => {
	const proxyUrl = process.env.REACT_APP_SF_TOKEN_PROXY_URL

	if (!proxyUrl) {
		throw new Error("REACT_APP_SF_TOKEN_PROXY_URL is not configured")
	}

	const controller = new AbortController()
	const timeoutId = setTimeout(() => controller.abort(), TOKEN_PROXY_TIMEOUT_MS)

	try {
		const response = await fetch(`${proxyUrl}`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			signal: controller.signal,
		})

		if (!response.ok) {
			console.error("Failed to obtain Salesforce token:", {
				status: response.status,
			})
			throw new Error("Failed to obtain Salesforce token")
		}

		const data = await response.json()

		if (!data.access_token || typeof data.access_token !== "string") {
			throw new Error("Token proxy response missing valid access_token")
		}

		if (!data.instance_url || typeof data.instance_url !== "string") {
			throw new Error("Token proxy response missing valid instance_url")
		}

		return {access_token: data.access_token, instance_url: data.instance_url}
	} catch (error) {
		if (error instanceof Error && error.name === "AbortError") {
			throw new Error(`Token proxy request timed out after ${TOKEN_PROXY_TIMEOUT_MS}ms`)
		}
		throw error
	} finally {
		clearTimeout(timeoutId)
	}
}
